import { of, Observable, noop } from 'rxjs';
import { switchMap, tap, retry, filter, distinctUntilChanged } from 'rxjs/operators';
import * as RA from 'ramda-adjunct';

export interface WrapConfig<T> {
    before: () => void;
    source$: Observable<T>;
    after?: (sourceSignal: T) => void;
}

export function wrap<T>(config: WrapConfig<T>): Observable<T> {
    const before$ = of(null).pipe(tap(config.before));
    return before$.pipe(switchMap(() => config.source$.pipe(tap(config.after || noop))));
}

export const standardRetry = () => (source$: Observable<any>) => source$.pipe(retry(1));

export const whenTrue = (source$: Observable<any>) => source$.pipe(filter(RA.isTrue));
export const whenFalse = (source$: Observable<any>) => source$.pipe(filter(RA.isFalse));

export const isTrue = () => whenTrue;
export const isFalse = () => whenFalse;

/**
 * Utility function to handle form submission with detail observable.
 * Returns an observable that will submit the form when subscribed to.
 * Uses distinctUntilChanged to prevent duplicate submissions of the same tag.
 *
 * @param detail$ - Observable containing the detail object with a tag property
 * @param form - Form component with a submit method that accepts a tag parameter
 * @returns Observable that submits the form and emits the detail
 *
 * @example
 * submitFormWithDetail(this.itemDetail$, this.itemTypesForm).subscribe();
 */
export function submitFormWithDetail<T extends { tag?: string }, F extends { submit: (tag: string) => void }>(
    detail$: Observable<T>,
    form: F | undefined
): Observable<T> {
    return detail$.pipe(
        distinctUntilChanged((a, b) => a.tag === b.tag),
        tap(detail => {
            if (detail?.tag && form) {
                form.submit(detail.tag);
            }
        })
    );
}
